import { useState } from 'react';
import { TrashIcon, PlusIcon } from '@phosphor-icons/react';
import useSWR from 'swr';
import toast from 'react-hot-toast';

import { useCustomerPortalCustomer } from '../CustomerPortalCustomerContext';
import { useTranslation } from '../../../contexts/translation-context';
import { useAuth } from '../../../contexts/auth-context';
import { getDisplayError } from '../../../utils/get-display-error';
import { ITableHeader, Table } from 'components/table/Table';
import { ConfirmDialog } from '../../../components/dialog/ConfirmDialog';
import { FormDialog } from '../../../components/dialog/FormDialog';
import { InputField } from 'components/input/InputField';
import { CheckboxField } from 'components/checkbox/CheckboxField';
import { SpinnerBlock } from '../../../components/Spinner';
import { EmptyState } from '../../../components/empty-state/EmptyState';
import { captureException } from '@sentry/react';
import { ErrorState } from '../../../components/error-state/ErrorState';
import { fetchCustomerPortalApiKeys, createCustomerPortalApiKey, revokeCustomerPortalApiKey } from './models';
import { API_KEY_SCOPES } from 'src/app/customer-api/utils/api-key-scopes';
import { getKeyStatus } from 'src/app/customer-api/utils/get-key-status';
import { ApiKeyStatusPill } from 'src/app/customer-api/components/ApiKeyStatusPill';
import { ApiKeyScopePill } from 'src/app/customer-api/components/ApiKeyScopePill';
import { CreatedKeyDialog } from 'src/app/customer-api/components/CreatedKeyDialog';

export const CustomerPortalApiKeysPage = () => {
  const { customer } = useCustomerPortalCustomer();
  const { me } = useAuth();
  const { i18n } = useTranslation();
  const [createdKey, setCreatedKey] = useState<string | null>(null);

  const isAdmin = me.customers.some((c) => c.customer.id === customer.id && c.role === 'CustomerAdmin');

  const {
    data: apiKeys,
    error,
    isLoading,
    mutate,
  } = useSWR(`customer-portal-api-keys-${customer.id}`, () => fetchCustomerPortalApiKeys(customer.id));

  const statusLabels = {
    active: i18n('customerPortal.apiKeys.active'),
    revoked: i18n('customerPortal.apiKeys.revoked'),
    expired: i18n('customerPortal.apiKeys.expired'),
  };

  const scopeLabels: Record<string, string> = {
    [API_KEY_SCOPES.ORDERS_READ]: i18n('customerPortal.apiKeys.ordersRead'),
    [API_KEY_SCOPES.ORDERS_WRITE]: i18n('customerPortal.apiKeys.ordersWrite'),
  };

  const createdKeyLabels = {
    title: i18n('customerPortal.apiKeys.apiKeyCreated'),
    warning: i18n('customerPortal.apiKeys.keyWarning'),
    copyButton: i18n('customerPortal.apiKeys.copyKey'),
    copiedToast: i18n('customerPortal.apiKeys.keyCopied'),
  };

  const tableHeaders: ITableHeader[] = [
    { id: 'name', name: i18n('customerPortal.apiKeys.name') },
    { id: 'prefix', name: i18n('customerPortal.apiKeys.prefix') },
    { id: 'scopes', name: i18n('customerPortal.apiKeys.scopes') },
    { id: 'status', name: i18n('customerPortal.apiKeys.status') },
    { id: 'lastUsed', name: i18n('customerPortal.apiKeys.lastUsed') },
    { id: 'createdAt', name: i18n('customerPortal.apiKeys.createdAt') },
    ...(isAdmin ? [{ id: 'actions', name: '' }] : []),
  ];

  if (isLoading) {
    return <SpinnerBlock message={i18n('customerPortal.loading')} />;
  }

  if (error) {
    return <ErrorState error={error} onRetry={() => mutate()} variant="section" />;
  }

  return (
    <div>
      {isAdmin && (
        <div className="flex justify-end mb-4">
          <FormDialog
            triggerText={
              <>
                <PlusIcon className="button-icon" />
                {i18n('customerPortal.apiKeys.createApiKey')}
              </>
            }
            triggerColor="primary"
            title={i18n('customerPortal.apiKeys.createApiKey')}
            submitText={i18n('customerPortal.apiKeys.createApiKey')}
            initialValues={{
              name: '',
              ordersRead: true,
              ordersWrite: false,
            }}
            onSubmit={async (values) => {
              const scopes: string[] = [];
              if (values.ordersRead) scopes.push(API_KEY_SCOPES.ORDERS_READ);
              if (values.ordersWrite) scopes.push(API_KEY_SCOPES.ORDERS_WRITE);

              if (scopes.length === 0) {
                throw new Error(i18n('customerPortal.apiKeys.scopeRequired'));
              }

              try {
                const result = await createCustomerPortalApiKey({
                  customerId: customer.id,
                  name: values.name,
                  scopes,
                });
                setCreatedKey(result.fullKey);
                mutate();
              } catch (err) {
                captureException(err);
                toast.error(
                  i18n('customerPortal.apiKeys.couldNotCreate', {
                    error: getDisplayError(err),
                  }),
                );
                throw err;
              }
            }}
          >
            <div className="mb-4">
              <InputField labelText={i18n('customerPortal.apiKeys.name')} name="name" type="text" />
            </div>
            <div className="mb-4">
              <div className="text-sm font-medium mb-2">{i18n('customerPortal.apiKeys.scopes')}</div>
              <div className="flex flex-col gap-2">
                <CheckboxField labelText={i18n('customerPortal.apiKeys.ordersRead')} name="ordersRead" />
                <CheckboxField labelText={i18n('customerPortal.apiKeys.ordersWrite')} name="ordersWrite" />
              </div>
            </div>
          </FormDialog>
        </div>
      )}

      {!apiKeys || apiKeys.length === 0 ? (
        <EmptyState title={i18n('customerPortal.apiKeys.noApiKeys')} />
      ) : (
        <Table
          idKey="id"
          headers={tableHeaders}
          data={apiKeys}
          mapData={(key) => {
            const status = getKeyStatus(key);
            const row: React.ReactNode[] = [
              key.name,
              <code className="text-xs" key="prefix">
                td_customer_{key.keyPrefix}...
              </code>,
              <div className="flex gap-1" key="scopes">
                {key.scopes.map((s) => (
                  <ApiKeyScopePill key={s} label={scopeLabels[s] ?? s} />
                ))}
              </div>,
              <ApiKeyStatusPill key="status" apiKey={key} labels={statusLabels} />,
              key.lastUsedAt ? new Date(key.lastUsedAt).toLocaleDateString() : i18n('customerPortal.apiKeys.never'),
              new Date(key.createdAt).toLocaleDateString(),
            ];

            if (isAdmin) {
              row.push(
                status === 'active' ? (
                  <ConfirmDialog
                    triggerColor="danger"
                    triggerText={<TrashIcon className="button-icon" />}
                    title={i18n('customerPortal.apiKeys.revokeApiKey')}
                    submitText={i18n('customerPortal.apiKeys.revokeApiKey')}
                    description={i18n('customerPortal.apiKeys.confirmRevoke', { name: key.name })}
                    onSubmit={async () => {
                      try {
                        await revokeCustomerPortalApiKey(key.id);
                        toast.success(i18n('customerPortal.apiKeys.apiKeyRevoked'));
                        mutate();
                      } catch (err) {
                        captureException(err);
                        toast.error(
                          i18n('customerPortal.apiKeys.couldNotRevoke', {
                            error: getDisplayError(err),
                          }),
                        );
                      }
                    }}
                  />
                ) : null,
              );
            }

            return row;
          }}
        />
      )}

      {createdKey && (
        <CreatedKeyDialog
          fullKey={createdKey}
          open={!!createdKey}
          onOpenChange={() => setCreatedKey(null)}
          labels={createdKeyLabels}
        />
      )}
    </div>
  );
};
